import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Ref } from "react";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import classNames from "classnames";
import { isReducedMotionEnabled } from "@constants";
import { CloseIcon } from "@icons";
import { Button } from "../Button/Button";
import { OverlayContainerProvider } from "@design-system/overlay/OverlayContainerProvider";
import { dismissLightOverlays } from "@design-system/overlay/lightDismiss";
import { registerOpenModal } from "@design-system/overlay/openModalLock";
import { Tooltip } from "../Tooltip/Tooltip";
import styles from "./Modal.module.scss";

interface ModalProps {
    show?: boolean;
    onClose?: () => void;
    hideCloseButton?: boolean;
    variant?: "standard" | "plain";
    size?: "sm" | "md" | "lg" | "xl";
    fullscreen?: boolean; // only applies to standard variant
    children?: React.ReactNode;
    centered?: boolean;
    className?: string;
    dialogClassName?: string;
    contentClassName?: string;
    bodyClassName?: string; // only applies to the internal body of the plain variant
    onHide?: () => void; // alias for onClose
    // Element to receive focus when the dialog opens, instead of Base UI's
    // default (the first tabbable element inside the popup).
    initialFocus?: React.ComponentProps<
        typeof BaseDialog.Popup
    >["initialFocus"];
    disableAnimation?: boolean;
    afterHideCallback?: () => void;
    onExited?: () => void;
    backdrop?: boolean | "static"; // Controls backdrop behavior
}

// Legacy interfaces for backward compatibility
interface StandardModalProps extends ModalProps {
    variant?: "standard";
}

interface PlainModalProps extends ModalProps {
    variant?: "plain";
}

const AFTER_HIDE_CALLBACK_DELAY = 10;

interface ModalHeaderProps {
    className?: string;
    children?: React.ReactNode;
}

interface ModalBodyProps {
    className?: string;
    children?: React.ReactNode;
    ref?: Ref<HTMLDivElement>;
}

interface ModalFooterProps {
    className?: string;
    children?: React.ReactNode;
}

export const StandardModalHeader: React.FC<ModalHeaderProps> = ({
    className,
    children,
    ...props
}) => (
    <div className={classNames(styles.header, className)} {...props}>
        {children}
    </div>
);

export const StandardModalBody = ({
    className,
    children,
    ref,
    ...props
}: ModalBodyProps) => (
    <div ref={ref} className={classNames(styles.body, className)} {...props}>
        {children}
    </div>
);

export const StandardModalFooter: React.FC<ModalFooterProps> = ({
    className,
    children,
    ...props
}) => (
    <div className={classNames(styles.footer, className)} {...props}>
        {children}
    </div>
);

export const Modal: React.FC<ModalProps> = (props) => {
    const {
        show = false,
        onClose,
        onHide,
        hideCloseButton,
        variant = "standard",
        className,
        fullscreen,
        size = "md",
        centered = true,
        dialogClassName,
        contentClassName,
        bodyClassName,
        initialFocus,
        disableAnimation,
        children,
        afterHideCallback,
        onExited,
        backdrop = true,
    } = props;

    // Support both onClose and legacy onHide
    const closeHandler = onClose ?? onHide;

    const handleOpenChange: NonNullable<
        React.ComponentProps<typeof BaseDialog.Root>["onOpenChange"]
    > = useCallback(
        (open: boolean) => {
            if (!open && closeHandler) {
                closeHandler();
                // Legacy callback support
                if (afterHideCallback) {
                    setTimeout(() => {
                        afterHideCallback();
                    }, AFTER_HIDE_CALLBACK_DELAY);
                }
            }
        },
        [closeHandler, afterHideCallback],
    );

    // Fires once the close animation has finished, whether the user dismissed
    // the dialog or the owner flipped `show` off - `onOpenChange` only covers
    // the former, which left consumers that reset state in `onExited` holding
    // a stale, still-mounted dialog after a programmatic close.
    const handleOpenChangeComplete = useCallback(
        (open: boolean) => {
            if (!open) {
                onExited?.();
            }
        },
        [onExited],
    );

    const isPointerDismissalDisabled = backdrop === "static" || fullscreen;

    const shouldAnimate = !disableAnimation && !isReducedMotionEnabled;
    const isPlain = variant === "plain";
    const isStandard = variant === "standard";
    const modalContentRef = useRef<HTMLDivElement>(null);
    const [container, setContainer] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (show) {
            setContainer(modalContentRef.current);
            return;
        }
        setContainer(null);
    }, [show]);

    useEffect(() => {
        if (!show) {
            return;
        }
        dismissLightOverlays();
        return registerOpenModal();
    }, [show]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                closeHandler?.();
            }
        },
        [closeHandler],
    );

    return (
        <BaseDialog.Root
            open={show}
            onOpenChange={handleOpenChange}
            onOpenChangeComplete={handleOpenChangeComplete}
            modal={backdrop !== false}
            disablePointerDismissal={isPointerDismissalDisabled}
        >
            <BaseDialog.Portal>
                {backdrop !== false && !fullscreen && (
                    <BaseDialog.Backdrop
                        forceRender
                        data-base-ui-swipe-ignore
                        className={classNames(styles.backdrop, {
                            [styles.show]: show,
                            [styles.fade]: shouldAnimate,
                        })}
                    />
                )}
                <div
                    data-base-ui-swipe-ignore
                    className={classNames(
                        styles.modal,
                        styles.fade,
                        {
                            [styles.standard]: isStandard,
                            [styles.plain]: isPlain,
                            [styles.fullscreen]: fullscreen && isStandard,
                        },
                        className,
                    )}
                >
                    <BaseDialog.Popup
                        data-base-ui-swipe-ignore
                        aria-describedby={undefined}
                        initialFocus={initialFocus}
                        className={classNames(styles.dialog, dialogClassName, {
                            [styles.sizeLg]: size === "lg",
                            [styles.sizeSm]: size === "sm",
                            [styles.sizeXl]: size === "xl",
                            [styles.centered]: centered && !fullscreen,
                        })}
                        onKeyDown={handleKeyDown}
                    >
                        <div
                            ref={modalContentRef}
                            className={classNames(
                                styles.content,
                                contentClassName,
                            )}
                        >
                            {closeHandler && !hideCloseButton && (
                                <Tooltip
                                    content="Close"
                                    shortcut="escape"
                                    side="bottom"
                                >
                                    <BaseDialog.Close
                                        render={(closeProps) => (
                                            <Button
                                                {...closeProps}
                                                aria-label="Close"
                                                className={
                                                    isPlain
                                                        ? styles.closeButtonPlain
                                                        : styles.closeButton
                                                }
                                                iconOnly
                                                size={isPlain ? "sm" : "md"}
                                                variant="secondary"
                                                tabIndex={-1}
                                            >
                                                <CloseIcon />
                                            </Button>
                                        )}
                                    />
                                </Tooltip>
                            )}
                            <OverlayContainerProvider
                                containerRef={modalContentRef}
                                container={container}
                            >
                                {isPlain ? (
                                    <StandardModalBody
                                        className={bodyClassName}
                                    >
                                        {children}
                                    </StandardModalBody>
                                ) : (
                                    children
                                )}
                            </OverlayContainerProvider>
                        </div>
                    </BaseDialog.Popup>
                </div>
            </BaseDialog.Portal>
        </BaseDialog.Root>
    );
};

// Legacy component aliases for backward compatibility
export const StandardModal: React.FC<StandardModalProps> = (props) => (
    <Modal {...props} variant="standard" />
);

export const PlainModal: React.FC<PlainModalProps> = (props) => (
    <Modal {...props} variant="plain" />
);
